import {captureException} from '@sentry/browser';

import {useEffect} from 'react';

import {isNil, isNotEmpty, isNotNil, keys} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useReadCodelistsQuery} from '@dms/api/core';

type ResourceId = 'sales' | 'rental';
type CodeListId = string;

const SORTED_LISTS: Record<CodeListId, ResourceId> = {
  // rental
  carPark: 'rental',
  equipment: 'rental',
  rentType: 'rental',
  // sales
  leasing_and_credit_company: 'sales',
  unsuccessful_business_case_reason: 'sales',
  customer_group: 'sales',
  unsuccessful_interest_reason: 'sales',
  interest_source: 'sales',
  vehicle_source: 'sales',
  payment_preference: 'sales',
};

export const useCodeLists = () => {
  const {data: codeList, ...queryStatus} = useReadCodelistsQuery({});

  useEffect(() => {
    if (isNilOrEmpty(codeList)) {
      return;
    }

    const allCodeListIds = keys(SORTED_LISTS);
    const missingCodeListIds = codeList?.filter(
      (item) => !allCodeListIds.includes(item.codelistId)
    );

    // NOTE: we want to sort codeLists by resource (business requirement), this cannot be done on BE, since it's not aware of the resource
    // so we need to do it on FE, and we need to make sure that all code lists are present
    // -----------------------------------
    // if you encounter this error, please add the missing code list to the SORTED_LISTS object
    if (isNotNil(missingCodeListIds) && isNotEmpty(missingCodeListIds)) {
      captureException(
        new Error(
          `New code lists returned from BE are not mapped in SORTED_LISTS: ${missingCodeListIds.map((item) => item.codelistId).join(', ')}. Add them to SORTED_LISTS in useCodeLists.ts with the correct ResourceId.`
        )
      );
    }
  }, [codeList]);

  const sortedCodeLists = codeList?.reduce(
    (acc, item) => {
      const resource = SORTED_LISTS[item.codelistId];

      if (isNil(resource)) {
        return acc;
      }

      const assignedItems = acc[resource];

      if (isNotNil(assignedItems)) {
        acc[resource].push(item);
      } else {
        acc[resource] = [item];
      }

      return acc;
    },
    {} as Record<ResourceId, {codelistId: string; name: string}[]>
  );

  return {data: sortedCodeLists, ...queryStatus};
};
